// src/hooks/useConfirm.js
// Hook for easy usage of confirmation modals

import { useState, useCallback } from 'react';

/**
 * useConfirm hook - Manages confirmation modal state
 *
 * Usage:
 * const { confirmState, showConfirm, hideConfirm } = useConfirm();
 *
 * // In your component:
 * <ConfirmModal
 *   isOpen={confirmState.isOpen}
 *   onClose={hideConfirm}
 *   onConfirm={() => { confirmState.onConfirm(); hideConfirm(); }}
 *   {...confirmState}
 * />
 *
 * // To show the modal:
 * showConfirm({
 *   title: 'Delete Item?',
 *   message: 'This cannot be undone.',
 *   type: 'danger',
 *   onConfirm: () => deleteItem(id)
 * });
 */
export const useConfirm = () => {
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: '',
    message: '',
    details: [],
    confirmText: 'Confirmer',
    cancelText: 'Annuler',
    type: 'warning',
    onConfirm: () => {},
  });

  const showConfirm = useCallback((options) => {
    setConfirmState({
      isOpen: true,
      title: options.title || "Confirmer l'action",
      message: options.message || '',
      details: options.details || [],
      confirmText: options.confirmText || 'Confirmer',
      cancelText: options.cancelText || 'Annuler',
      type: options.type || 'warning',
      onConfirm: options.onConfirm || (() => {}),
    });
  }, []);

  const hideConfirm = useCallback(() => {
    setConfirmState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const handleConfirm = useCallback(() => {
    confirmState.onConfirm();
    hideConfirm();
  }, [confirmState, hideConfirm]);

  return {
    confirmState,
    showConfirm,
    hideConfirm,
    handleConfirm,
  };
};

export default useConfirm;
